'use client';
/**
 * DÉPLOIEMENT DES RUBRIQUES — l'écran qui manquait.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Ce que cet écran répare ────────────────────────────────────────────────
 *
 * « Je voudrais commencer à réfléchir à la mise en place de l'outil, en
 * masquant au début les fonctionnalités sur lesquelles personne n'est formé. »
 * (RQ, 26/08/2026)
 *
 * Le mécanisme a été livré ce jour-là — `POST /ged/deploiement`, gardé par le
 * rôle `rq`, motif obligatoire, journal. Mais AUCUN écran ne l'appelait. Six
 * rubriques ont donc été fermées par script, et la RQ ne pouvait ni en fermer
 * une de plus, ni surtout en ROUVRIR une le jour où l'équipe serait formée.
 * Constaté par l'audit des droits du 08/09/2026.
 *
 * ── ⚠️ CE N'EST PAS UN DROIT, ET L'ÉCRAN DOIT LE DIRE ──────────────────────
 *
 * Il vit dans le groupe « Rôles & accès » parce que c'est là qu'on va le
 * chercher — mais le déploiement est un axe SÉPARÉ :
 *
 *   · il est GLOBAL : une rubrique en attente est fermée à tout le monde, RQ
 *     compris, parce que personne n'a été formé à s'en servir ;
 *   · il laisse les DROITS INTACTS dessous, ce qui est exactement ce qui
 *     permet de rouvrir d'un seul geste sans se rappeler qui y avait accès ;
 *   · il produit un autre message : « ce n'est pas encore ouvert », jamais
 *     « accès refusé » — le second enverrait la personne réclamer un droit que
 *     personne ne peut lui donner.
 *
 * Masquer une rubrique en retirant les droits paraît plus simple et coûte deux
 * fois : la matrice est écrasée pour un motif qui n'a rien à voir avec elle, et
 * l'écran accuse quelqu'un qui n'a rien fait. D'où le bandeau, en tête.
 *
 * ── ⚠️ L'ÉCRAN N'A PAS SA PROPRE RÈGLE ─────────────────────────────────────
 *
 * Il n'invente ni l'état d'une rubrique, ni le résumé, ni les rappels : tout
 * vient de `GET /ged/deploiement`. Les seuls calculs locaux — regroupement,
 * recevabilité du motif, aperçu de la phrase — vivent dans
 * `lib/deploiement-vue.ts`, qui est pur et vérifié par `npm test` contre le
 * moteur lui-même.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { card, I, Skeleton, useConfirmation } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';
import {
  grouperParFamille, empechementFermeture, apercuPhrase, origineFermeture,
  type RubriqueDeploiement,
} from '@/lib/deploiement-vue';

import { Bouton } from '@/shared/Bouton';
type Rappel = { code: string; label: string; motif: string; compte: number; phrase: string };
type Etat = {
  rubriques: RubriqueDeploiement[];
  enAttente: string[];
  resume: string;
  rappels?: Rappel[];
};

export function Deploiement() {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState('');
  /** La rubrique dont on est en train d'écrire le motif de fermeture. */
  const [ferme, setFerme] = useState<{ code: string; motif: string; datePrevue: string } | null>(null);
  const { demander, confirmation } = useConfirmation();

  const charger = useCallback(async () => {
    try {
      const r = await fetch('/api/ged/ged/deploiement', { cache: 'no-store' });
      const d = await r.json();
      if (!r.ok) { setErreur(d.error?.message || d.error || 'Lecture refusée.'); return; }
      setErreur('');
      setEtat(d);
    } catch {
      setErreur('Le service est injoignable. Rien n’a été modifié.');
    }
  }, []);

  /* ⚠️ Faux positif assumé de `react-hooks/set-state-in-effect` : `charger` est
     un `useCallback` ASYNC, son `setState` se produit après un `await`, donc
     dans une continuation — jamais dans le corps de l'effet. Silence CIBLÉ,
     ligne à ligne : couper la règle pour le fichier masquerait un vrai cas
     ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(); }, [charger]);

  if (erreur && !etat) return <div style={{ ...card, padding: 14, fontSize: 13 }} role="alert">{erreur}</div>;
  if (!etat) return <Skeleton rows={6} />;

  async function declarer(code: string, etatVoulu: 'service' | 'attente', motif = '', datePrevue = '') {
    setEnvoi(code); setMessage('');
    try {
      const r = await fetchEcriture('/api/ged/ged/deploiement', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, etat: etatVoulu, motif, datePrevue }),
      });
      const d = await r.json();
      if (!r.ok) { setMessage(d.error?.message || d.error || 'La déclaration a échoué.'); return; }
      /* Le message vient du MOTEUR : c'est lui qui sait ce qu'il a écrit, et
         une phrase recomposée ici dirait « fermée » d'une manœuvre refusée. */
      setMessage(d.message || 'Enregistré.');
      setFerme(null);
      await charger();
    } catch {
      setMessage('Le service est injoignable. Rien n’a été enregistré.');
    } finally { setEnvoi(''); }
  }

  const familles = grouperParFamille(etat.rubriques || []);
  const nbAttente = (etat.enAttente || []).length;

  return (
    <div>
      {confirmation}

      {/* ⚠️ LA NATURE DE L'AXE, DITE AVANT LA LISTE. Sous le tableau, on aurait
          déjà pris la liste pour une matrice de droits — la confusion exacte
          que ce mécanisme existe pour éviter. */}
      <div style={{
        ...card, padding: '12px 15px', marginBottom: 16, fontSize: 12.5, lineHeight: 1.65,
        background: 'var(--cyan-50)', borderColor: 'var(--cyan-200)',
        color: 'var(--text-strong)',
      }}>
        <strong>Le déploiement n’est pas un droit.</strong> Une rubrique mise en attente disparaît
        pour <strong>tout le monde</strong>, vous comprise — parce que personne n’a encore été formé
        à s’en servir. Les droits restent intacts dessous : c’est ce qui permet de la rouvrir d’un
        seul geste, sans avoir à se rappeler qui devait y avoir accès. La personne qui tombe dessus
        lit « ce n’est pas encore ouvert », jamais « accès refusé ».
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' }}>
        {etat.resume}
      </p>

      {/* Les rappels : le motif écrit à la fermeture décrit un état du monde,
          et cet état change sans que rien ne le dise. Servis par la même route,
          donc affichés ici — c'est le seul écran d'où l'on peut agir dessus. */}
      {!!etat.rappels?.length && (
        <div style={{
          ...card, padding: '11px 14px', marginBottom: 16, display: 'grid', gap: 7,
          background: 'var(--amber-50)', borderColor: 'var(--amber-300)',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
            <I n="alert" s={15} />
            {etat.rappels.length === 1
              ? 'Une rubrique est fermée pour un motif qui n’est plus d’actualité.'
              : `${etat.rappels.length} rubriques sont fermées pour des motifs qui ne sont plus d’actualité.`}
          </div>
          {etat.rappels.map((r) => (
            <div key={r.code} style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
              {r.phrase}
              {r.motif && (
                <span style={{ display: 'block', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Motif écrit à la fermeture : « {r.motif} »
                </span>
              )}
            </div>
          ))}
          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
            Ceci ne dit pas qu’il faut ouvrir : seulement que le fait sur lequel la décision reposait
            a changé. Une rubrique peut rester fermée à bon droit.
          </div>
        </div>
      )}

      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {familles.map(({ famille, rubriques }) => (
          <React.Fragment key={famille}>
            <div style={{
              padding: '9px 12px 5px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
              letterSpacing: '.05em', color: 'var(--text-muted)', background: 'var(--surface-sunken)',
            }}>{famille}</div>

            {rubriques.map((r) => {
              const enEdition = ferme?.code === r.code;
              const empechement = enEdition ? empechementFermeture(ferme.motif, ferme.datePrevue) : null;
              return (
                <div key={r.code} style={{
                  padding: '9px 12px', borderTop: '1px solid var(--border-subtle)',
                  display: 'grid', gap: 6,
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ flex: 1, minWidth: 190, fontSize: 13, color: 'var(--text-strong)' }}>
                      {r.label}
                      {!r.enService && (
                        <span style={{
                          marginLeft: 8, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '.04em', padding: '1px 6px', borderRadius: 'var(--radius-xs)',
                          background: 'var(--amber-100)', color: 'var(--amber-800)',
                        }}>en attente</span>
                      )}
                    </span>

                    {r.enService ? (
                      <Bouton taille="sm" disabled={!!envoi} onClick={() => { setFerme({ code: r.code, motif: '', datePrevue: '' }); setMessage(''); }}>
                        Mettre en attente
                      </Bouton>
                    ) : (
                      /* Rouvrir se confirme, mais ne se motive PAS : l'état
                         normal d'une rubrique est d'être ouverte, et le moteur
                         EFFACE la déclaration au lieu d'écrire « service ». */
                      <Bouton variante="primaire" taille="sm" disabled={!!envoi} onClick={() => void demander(
                          `« ${r.label} » réapparaîtra immédiatement pour toutes les personnes qui y ont droit. `
                          + 'Leurs droits n’ont pas changé pendant la fermeture : rien d’autre n’est à régler.',
                          { titre: `Ouvrir « ${r.label} » ?`, confirmer: 'Ouvrir' },
                        ).then((oui) => { if (oui) void declarer(r.code, 'service'); })}>
                        {envoi === r.code ? 'Ouverture…' : 'Ouvrir'}
                      </Bouton>
                    )}
                  </div>

                  {/* L'état d'une rubrique fermée : la phrase servie par le
                      moteur, puis l'origine — quand on la connaît. */}
                  {!r.enService && !enEdition && (
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
                      {r.phrase}
                      {origineFermeture(r) && (
                        <span style={{ display: 'block', fontSize: 11.5 }}>{origineFermeture(r)}</span>
                      )}
                    </div>
                  )}

                  {enEdition && (
                    <div style={{ display: 'grid', gap: 8, padding: '4px 0 2px' }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-body)' }}>
                        Motif — c’est la phrase que <strong>lira la personne</strong> qui tombe sur la
                        rubrique, pas une note interne.
                      </label>
                      <textarea
                        value={ferme.motif} rows={2} autoFocus
                        onChange={(e) => setFerme({ ...ferme, motif: e.target.value })}
                        placeholder="Personne n’est encore formé à cette rubrique ; elle ouvrira après la session de mars."
                        style={{
                          width: '100%', fontSize: 13, padding: '7px 9px', lineHeight: 1.5,
                          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                          fontFamily: 'inherit', resize: 'vertical',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12, color: 'var(--text-body)' }}>
                          Ouverture prévue{' '}
                          <input
                            type="date" value={ferme.datePrevue}
                            onChange={(e) => setFerme({ ...ferme, datePrevue: e.target.value })}
                            style={{
                              fontSize: 12.5, padding: '4px 7px', fontFamily: 'inherit',
                              border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                            }}
                          />
                        </label>
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                          facultatif — vide si la date n’est pas décidée
                        </span>
                      </div>

                      {/* ⚠️ L'APERÇU DE LA PHRASE FINIE. On ne demande pas
                          d'écrire un motif à l'aveugle : on montre le texte tel
                          qu'il s'affichera, avant de fermer quoi que ce soit. */}
                      <div style={{
                        ...card, padding: '9px 12px', fontSize: 12.5, lineHeight: 1.55,
                        background: 'var(--surface-sunken)', color: 'var(--text-body)',
                      }}>
                        <span style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 3 }}>
                          Ce que lira la personne
                        </span>
                        {apercuPhrase(r.label, ferme.motif, ferme.datePrevue)}
                      </div>

                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Bouton variante="primaire" taille="sm" disabled={!!empechement || !!envoi} onClick={() => void demander(
                            `« ${r.label} » disparaîtra de la barre latérale de TOUTES les personnes qui `
                            + 'y ont droit, vous comprise. Leurs droits ne changent pas : vous pourrez la '
                            + 'rouvrir d’un seul geste depuis cet écran.',
                            { titre: `Mettre « ${r.label} » en attente ?`, confirmer: 'Mettre en attente' },
                          ).then((oui) => { if (oui) void declarer(r.code, 'attente', ferme.motif, ferme.datePrevue); })}>
                          {envoi === r.code ? 'Enregistrement…' : 'Mettre en attente'}
                        </Bouton>
                        <Bouton taille="sm" disabled={!!envoi} onClick={() => setFerme(null)}>
                          Annuler
                        </Bouton>
                        {/* ⚠️ JAMAIS UN BOUTON GRISÉ SANS PHRASE : ce qui manque
                            se dit, sinon la personne croit à une panne. */}
                        {empechement && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{empechement}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {nbAttente === 0
            ? 'Aucune rubrique n’est en attente.'
            : `${nbAttente} rubrique(s) en attente.`}
        </span>
        {message && (
          <span style={{ fontSize: 12.5, color: message.includes('échoué') || message.includes('injoignable') ? 'var(--red-600)' : 'var(--green-700)' }}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
}
